import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Loader2 } from "lucide-react";

const STAGES = [
  { key: "thesis", label: "1. Thesis", hint: "One clear arguable sentence." },
  { key: "outline", label: "2. Outline", hint: "3 supporting points that back the thesis." },
  { key: "body", label: "3. Draft", hint: "Write your full essay. Aim 300+ words." },
  { key: "revision", label: "4. Revise", hint: "Notes on what to fix, clearer transitions." },
];

export default function EssayCoach() {
  const { user, refresh } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [d, setD] = useState({ id: null, title: "", prompt: "", thesis: "", outline: "", body: "", revision_notes: "" });
  const [stage, setStage] = useState("thesis");
  const [feedback, setFeedback] = useState("");
  const [loadingFb, setLoadingFb] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => { api.get("/essay/drafts").then(({ data }) => setDrafts(data)); }, []);

  // autosave (debounced 1.2s)
  useEffect(() => {
    if (!d.title && !d.body && !d.thesis) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/essay/drafts", d);
        setD((prev) => ({ ...prev, id: data.id }));
      } catch {/* silent */}
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [d.title, d.prompt, d.thesis, d.outline, d.body, d.revision_notes]);

  const loadDraft = async (id) => {
    const { data } = await api.get(`/essay/drafts/${id}`);
    setD(data);
    setFeedback("");
    setStage("thesis");
  };

  const newDraft = () => {
    setD({ id: null, title: "", prompt: "", thesis: "", outline: "", body: "", revision_notes: "" });
    setFeedback("");
    setStage("thesis");
  };

  const stageValue = () => stage === "revision" ? d.revision_notes : d[stage];
  const setStageValue = (v) => setD((prev) => ({ ...prev, [stage === "revision" ? "revision_notes" : stage]: v }));

  const getFeedback = async () => {
    if (!stageValue()?.trim()) return toast.error("Write something first");
    setLoadingFb(true); setFeedback("");
    try {
      const { data } = await api.post("/essay/feedback", { stage, content: stageValue(), prompt: d.prompt });
      setFeedback(data.feedback);
    } catch (e) { toast.error(e?.response?.data?.detail || "Coach unavailable"); }
    finally { setLoadingFb(false); refresh(); }
  };

  return (
    <div className="space-y-6" data-testid="essay-coach">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="chip mb-2">Essay coach</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Write, get feedback, revise.</h1>
          <p className="text-slate-600 text-sm">Autosaves as you type. AI gives tiny nudges — never rewrites for you.</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="essay-open-draft" onChange={(e) => e.target.value && loadDraft(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
            <option value="">Open draft…</option>
            {drafts.map((x) => <option key={x.id} value={x.id}>{x.title || "Untitled"}</option>)}
          </select>
          <button data-testid="essay-new" onClick={newDraft} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold">New</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <input data-testid="essay-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="Essay title"
          className="px-4 py-3 rounded-xl border border-slate-200" />
        <input data-testid="essay-prompt" value={d.prompt} onChange={(e) => setD({ ...d, prompt: e.target.value })} placeholder="Assignment prompt (optional)"
          className="px-4 py-3 rounded-xl border border-slate-200" />
      </div>

      <div className="flex flex-wrap gap-2 rounded-full bg-slate-100 p-1 w-fit">
        {STAGES.map((s) => (
          <button key={s.key} data-testid={`essay-stage-${s.key}`} onClick={() => { setStage(s.key); setFeedback(""); }}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${stage === s.key ? "bg-white text-teal-800 shadow-sm" : "text-slate-500"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="p-4 rounded-2xl bg-sky-50 border border-sky-100 text-sm text-slate-700">
        <span className="font-semibold">{STAGES.find((s) => s.key === stage)?.label}:</span> {STAGES.find((s) => s.key === stage)?.hint}
      </div>

      <textarea data-testid={`essay-textarea-${stage}`} rows={stage === "body" ? 14 : 6} value={stageValue() || ""}
        onChange={(e) => setStageValue(e.target.value)}
        placeholder={`Write your ${stage}…`}
        className="w-full px-4 py-3 rounded-2xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none leading-relaxed" />

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><Save size={12} /> Autosaves as you type</span>
        <span>·</span>
        <span data-testid="essay-word-count">{(stageValue() || "").trim().split(/\s+/).filter(Boolean).length} words</span>
      </div>

      <button data-testid="essay-feedback-btn" onClick={getFeedback} disabled={loadingFb}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-70">
        {loadingFb ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
        {loadingFb ? "Coach is reading…" : "Get feedback"}
      </button>

      {feedback && (
        <div data-testid="essay-feedback" className="p-5 rounded-2xl bg-amber-50 border border-amber-200 whitespace-pre-wrap text-slate-800 leading-relaxed">
          {feedback}
        </div>
      )}
    </div>
  );
}
